import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { estudianteService } from '../services/estudianteService';

/**
 * Los estudiantes visibles. Con `periodo` ("AAAA.S"), los que estuvieron ese
 * semestre y el proceso que tuvieron **ahí**, no el último.
 */
export function useEstudiantes(periodo?: string) {
  return useQuery({
    queryKey: ['estudiantes', periodo ?? ''],
    queryFn: () => estudianteService.list(periodo),
    placeholderData: keepPreviousData,
  });
}

export function useEstudiante(rut: number) {
  return useQuery({
    queryKey: ['estudiante', rut],
    queryFn: () => estudianteService.get(rut),
    enabled: Number.isFinite(rut),
  });
}

/**
 * El coordinador le quita el tema al estudiante.
 *
 * Refresca también temas y procesos: el tema vuelve al catálogo como disponible y
 * los procesos abiertos del estudiante se quedan sin tema ni guía.
 */
export function useRevocarTema(rut: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (motivo: string) => estudianteService.revocarTema(rut, motivo),
    onSuccess: (ficha) => {
      qc.setQueryData(['estudiante', rut], ficha);
      qc.invalidateQueries({ queryKey: ['estudiantes'] });
      qc.invalidateQueries({ queryKey: ['temas'] });
      qc.invalidateQueries({ queryKey: ['instancias'] });
    },
  });
}

/** El coordinador cambia si el estudiante puede publicar su memoria. */
export function useSetPublicarMemoria(rut: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (valor: boolean) => estudianteService.setPublicarMemoria(rut, valor),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['estudiante', rut] }),
  });
}
